<template>
	<section class="min-h-screen py-8 px-4 sm:px-8">
		<div class="mx-auto grid w-full max-w-6xl grid-cols-1 overflow-hidden rounded-3xl border border-slate-200 shadow-xl xl:grid-cols-2">
			<div class="hidden xl:flex flex-col justify-between p-12 border-r border-slate-200 bg-gradient-to-br from-[#E7A329] via-[#D68D0A] to-[#CD7D00]">
				<div>
					<p class="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-white">Self Ship</p>
					<h2 class="text-4xl font-bold leading-tight text-white">Account Recovery</h2>
					<p class="mt-4 text-white">
						Recover your account in three secure steps and set a new password quickly.
					</p>
				</div>
				<div class="space-y-2 text-sm text-white">
					<p>Step-by-step guided flow</p>
					<p>Secure OTP verification</p>
				</div>
			</div>

			<div class="px-6 py-10 sm:px-10 lg:px-14">
				<div class="mx-auto w-full max-w-md">
					<router-link to="/" class="mb-8 inline-flex xl:hidden">
						<img src="../assets/images/logo-nav.png" alt="navbar" class="h-10 object-contain" />
					</router-link>

					<h1 class="text-3xl font-bold text-slate-900">Forgot Password</h1>
					<p class="mt-2 text-sm text-slate-500">{{ stepTitle }}</p>

					<div class="mt-5">
						<div class="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-500">
							<span>Step {{ step }} of 3</span>
							<span>{{ progressWidth }}</span>
						</div>
						<div class="h-2 rounded-full bg-slate-200">
							<div
								class="h-full rounded-full bg-gradient-to-r from-[#E7A329] via-[#D68D0A] to-[#CD7D00] transition-all duration-300"
								:style="{ width: progressWidth }"
							></div>
						</div>
					</div>

					<form class="mt-8 space-y-5" @submit.prevent="handleSubmit">
						<DangerAlert :message="errors.message" />

						<div v-if="step === 1" class="space-y-2">
							<label for="forgot-email" class="text-sm font-semibold text-slate-700">Email</label>
							<input
								id="forgot-email"
								v-model="form.email"
								type="email"
								placeholder="name@example.com"
								class="h-12 w-full rounded-xl border border-slate-300 px-4 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
							/>
							<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.email }}</span>
						</div>

						<template v-if="step === 2">
							<div class="space-y-2">
								<label for="verify-email" class="text-sm font-semibold text-slate-700">Email</label>
								<input
									id="verify-email"
									v-model="form.email"
									type="email"
									placeholder="name@example.com"
									class="h-12 w-full rounded-xl border border-slate-300 px-4 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
								/>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.email }}</span>
							</div>
							<div class="space-y-2">
								<label for="verify-otp" class="text-sm font-semibold text-slate-700">OTP</label>
								<input
									id="verify-otp"
									v-model="form.otp"
									type="text"
									maxlength="6"
									placeholder="Enter 6-digit OTP"
									class="h-12 w-full rounded-xl border border-slate-300 px-4 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
								/>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.otp }}</span>
							</div>
						</template>

						<template v-if="step === 3">
							<div class="space-y-2">
								<label for="reset-email" class="text-sm font-semibold text-slate-700">Email</label>
								<input
									id="reset-email"
									v-model="form.email"
									type="email"
									placeholder="name@example.com"
									class="h-12 w-full rounded-xl border border-slate-300 px-4 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
								/>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.email }}</span>
							</div>
							<div class="space-y-2">
								<label for="reset-otp" class="text-sm font-semibold text-slate-700">OTP</label>
								<input
									id="reset-otp"
									v-model="form.otp"
									type="text"
									maxlength="6"
									placeholder="Enter 6-digit OTP"
									class="h-12 w-full rounded-xl border border-slate-300 px-4 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
								/>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.otp }}</span>
							</div>
							<div class="space-y-2">
								<label for="new-password" class="text-sm font-semibold text-slate-700">New Password</label>
								<div class="relative">
									<input
										id="new-password"
										v-model="form.password"
										:type="showPassword ? 'text' : 'password'"
										placeholder="Enter your new password"
										class="h-12 w-full rounded-xl border border-slate-300 px-4 pr-11 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
									/>
									<img
										@click="toggleShowPassword"
										class="absolute right-3 top-3 cursor-pointer opacity-70 hover:opacity-100"
										src="../assets/icons/icon-eye-open.svg"
										alt="pass-eye"
									/>
								</div>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.password }}</span>
							</div>
							<div class="space-y-2">
								<label for="confirm-password" class="text-sm font-semibold text-slate-700">Confirm Password</label>
								<div class="relative">
									<input
										id="confirm-password"
										v-model="form.password_confirmation"
										:type="showPassword ? 'text' : 'password'"
										placeholder="Confirm your password"
										class="h-12 w-full rounded-xl border border-slate-300 px-4 pr-11 text-slate-900 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-200"
									/>
									<img
										@click="toggleShowPassword"
										class="absolute right-3 top-3 cursor-pointer opacity-70 hover:opacity-100"
										src="../assets/icons/icon-eye-open.svg"
										alt="pass-eye"
									/>
								</div>
								<span class="text-sm font-medium text-slate-700">{{ errors?.errors?.password_confirmation }}</span>
							</div>
						</template>

						<button
							type="submit"
							:disabled="loading"
							class="h-12 w-full rounded-xl border border-slate-900 text-base font-semibold text-slate-900 shadow-sm transition hover:shadow-md disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-slate-400"
						>
							{{ loading ? "Loading ..." : submitLabel }}
						</button>
					</form>

					<div class="mt-6 flex items-center justify-between text-sm">
						<span
							v-if="step > 1"
							@click="goBackStep"
							class="cursor-pointer font-semibold text-slate-600 hover:text-slate-800"
						>
							Back
						</span>
						<span v-else></span>
						<span @click="goToLogin" class="cursor-pointer font-semibold text-slate-700 hover:text-slate-900">Back to Login</span>
					</div>
				</div>
			</div>
		</div>
		<ShippingExpertsSection />
	</section>
</template>

<script>
import DangerAlert from "./Global/Alerts/DangerAlert.vue";
import ShippingExpertsSection from "./ShippingExpertsSection.vue";

export default {
	name: "ForgotPasswordComponent",
	components: { DangerAlert, ShippingExpertsSection },
	data() {
		return {
			step: 1,
			loading: false,
			showPassword: false,
			errors: [],
			form: {
				email: "",
				otp: "",
				password: "",
				password_confirmation: "",
			},
		};
	},
	computed: {
		stepTitle() {
			if (this.step === 1) return "Request OTP";
			if (this.step === 2) return "Verify OTP";
			return "Reset Password";
		},
		progressWidth() {
			return `${Math.round((this.step / 3) * 100)}%`;
		},
		submitLabel() {
			if (this.step === 1) return "Send OTP";
			if (this.step === 2) return "Verify OTP";
			return "Reset Password";
		},
	},
	methods: {
		goToLogin() {
			this.$router.push({ name: "login" });
		},
		goBackStep() {
			this.errors = [];
			this.step -= 1;
		},
		toggleShowPassword() {
			this.showPassword = !this.showPassword;
		},
		handleSubmit() {
			this.loading = true;
			this.errors = [];

			let request;

			if (this.step === 1) {
				request = this.$axios.post("forgot-password", {
					email: this.form.email,
				});
			} else if (this.step === 2) {
				request = this.$axios.post("forgot-password/verify-otp", {
					email: this.form.email,
					otp: this.form.otp,
				});
			} else {
				request = this.$axios.post("reset-password", {
					email: this.form.email,
					otp: this.form.otp,
					password: this.form.password,
					password_confirmation: this.form.password_confirmation,
				});
			}

			request
				.then(() => {
					if (this.step < 3) {
						this.step += 1;
						return;
					}

					this.$router.push({ name: "login" });
				})
				.catch((error) => {
					this.errors = error?.response?.data || { message: "Something went wrong. Please try again." };
				})
				.finally(() => {
					this.loading = false;
				});
		},
	},
};
</script>
